import React, { useState, useRef, useEffect } from 'react';
import DataTable from './shared/DataTable';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ChatSidebar from './ChatSidebar';
import { useChatCable } from './Chat/hooks/useChatCable';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ChatBubble from './Chat/ChatBubble';

interface Message {
  id: number;
  text: string;
  sender: 'user' | 'bot';
  created_at: string;
  artifacts: Artifact[];
}

export interface ChatThread {
  id: number;
  name: string;
  last_message: string;
  created_at: string;
  updated_at: string;
}

interface Artifact {
  id: number;
  artifact_type: string;
  data: {
    columns: Array<{ key: string; header: string }>;
    rows: Array<any>;
  };
  title: string;
  description?: string;
  created_at: string;
}

const Chat: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [newMessage, setNewMessage] = useState('');
  const [showRightPanel, setShowRightPanel] = useState(false);
  const [selectedThread, setSelectedThread] = useState<number | null>(null);
  const [editingThread, setEditingThread] = useState<ChatThread | null>(null);
  const [isAiTyping, setIsAiTyping] = useState(false);
  const chatInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [selectedArtifact, setSelectedArtifact] = useState<Artifact | null>(null);

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  // Format message text with line breaks
  const formatMessageText = (text: string) => {
    return text.split('\n').map((line, i) => (
      <React.Fragment key={i}>
        {line}
        {i !== text.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };

  // Query for threads
  const { data: threads = [] } = useQuery({
    queryKey: ['threads'],
    queryFn: async () => {
      const response = await axios.get('/api/chat_threads');
      if (response.data.length > 0 && !selectedThread) {
        setSelectedThread(response.data[0].id);
      }
      return response.data;
    },
  });

  // Query for messages
  const { data: messages = [] } = useQuery({
    queryKey: ['messages', selectedThread],
    queryFn: async () => {
      if (!selectedThread) return [];
      const response = await axios.get(`/api/chat_threads/${selectedThread}/messages`);
      return response.data;
    },
    enabled: !!selectedThread,
    onSuccess: () => {
      // Scroll immediately when messages are loaded
      scrollToBottom();
    },
  });

  // Scroll when new messages are added (for streaming responses)
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Mutation for sending messages
  const sendMessageMutation = useMutation({
    mutationFn: async ({ threadId, text }: { threadId: number; text: string }) => {
      setIsAiTyping(true);
      try {
        const response = await axios.post(
          `/api/chat_threads/${threadId}/messages`,
          {
            message: {
              text,
              sender: 'user',
            },
          },
          {
            responseType: 'stream',
            onDownloadProgress: (progressEvent) => {
              const events = progressEvent.event.target.responseText
                .split('\n')
                .filter((line) => line.startsWith('data: '));

              events.forEach((eventLine) => {
                try {
                  const event = JSON.parse(eventLine.slice(6));
                  // Update the message in the cache
                  queryClient.setQueryData(['messages', selectedThread], (oldData: Message[] | undefined) => {
                    if (!oldData) return oldData;
                    return oldData.map((msg) =>
                      msg.id === event.messageId ? { ...msg, text: msg.text + event.content } : msg,
                    );
                  });
                } catch (e) {
                  console.error('Error parsing SSE:', e);
                }
              });
            },
          },
        );
        return response.data;
      } finally {
        setIsAiTyping(false);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages', selectedThread] });
      queryClient.invalidateQueries({ queryKey: ['threads'] });
    },
  });

  // Mutation for creating threads
  const createThreadMutation = useMutation({
    mutationFn: async (name: string) => {
      const response = await axios.post('/api/chat_threads', {
        chat_thread: { name },
      });
      return response.data;
    },
    onSuccess: (newThread) => {
      queryClient.invalidateQueries({ queryKey: ['threads'] });
      setSelectedThread(newThread.id);
      setTimeout(() => chatInputRef.current?.focus(), 0);
    },
  });

  // Mutation for updating threads
  const updateThreadMutation = useMutation({
    mutationFn: async ({ threadId, name }: { threadId: number; name: string }) => {
      const response = await axios.put(`/api/chat_threads/${threadId}`, {
        chat_thread: { name },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['threads'] });
      setEditingThread(null);
    },
  });

  // Mutation for deleting threads
  const deleteThreadMutation = useMutation({
    mutationFn: async (threadId: number) => {
      await axios.delete(`/api/chat_threads/${threadId}`);
      return threadId;
    },
    onSuccess: (deletedThreadId) => {
      queryClient.invalidateQueries({ queryKey: ['threads'] });
      if (selectedThread === deletedThreadId) {
        const nextThread = threads.find((t) => t.id !== deletedThreadId);
        setSelectedThread(nextThread?.id || null);
      }
    },
  });

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedThread) return;

    sendMessageMutation.mutate(
      { threadId: selectedThread, text: newMessage },
      {
        onSuccess: () => {
          setNewMessage('');
        },
      },
    );
  };

  const updateThread = (threadId: number, newName: string) => {
    updateThreadMutation.mutate({ threadId, name: newName });
  };

  const deleteThread = (threadId: number) => {
    if (!window.confirm('Are you sure you want to delete this thread?')) return;
    deleteThreadMutation.mutate(threadId);
  };

  const toggleRightPanel = () => {
    setShowRightPanel(!showRightPanel);
  };

  const tableData = Array.from({ length: 20 }).map((_, index) => ({
    id: index + 1,
    title: `Sample Item ${index + 1}`,
    status: index % 2 === 0 ? 'Active' : 'Pending',
    date: new Date().toLocaleDateString(),
  }));

  const tableColumns = [
    { key: 'id', header: 'ID' },
    { key: 'title', header: 'Title' },
    { key: 'status', header: 'Status' },
    { key: 'date', header: 'Date' },
  ];

  const handleOrganizationClick = (organization: any) => {
    navigate(`/organizations/${organization.id}`);
  };

  const renderArtifact = (artifact: Artifact) => {
    switch (artifact.artifact_type) {
      case 'table':
        return (
          <DataTable
            data={artifact.data.rows}
            columns={artifact.data.columns}
            onRowClick={handleOrganizationClick}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex h-full flex-grow">
      <ChatSidebar
        threads={threads}
        selectedThread={selectedThread}
        editingThread={editingThread}
        onThreadSelect={setSelectedThread}
        onNewThread={() => {
          createThreadMutation.mutate('Untitled');
        }}
        onEditThread={setEditingThread}
        onUpdateThread={updateThread}
        onDeleteThread={deleteThread}
      />

      {/* Main Chat Area */}
      {selectedThread && (
        <div className="flex flex-col flex-grow bg-gray-100 dark:bg-gray-900 h-full">
          {/* Dark mode and right panel toggles */}
          <div className="p-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            {selectedThread ? (
              <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
                {threads.find((t) => t.id === selectedThread)?.name}
              </h2>
            ) : (
              <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Chat</h2>
            )}
          </div>

          <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
            {messages.map((message, index) => (
              <ChatBubble
                key={message.id}
                message={message}
                isLast={index === messages.length - 1}
                onArtifactSelect={setSelectedArtifact}
              />
            ))}
            {isAiTyping && (
              <div className="flex justify-start">
                <div className="max-w-xs md:max-w-md p-3 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                  <div className="flex space-x-2">
                    <div
                      className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"
                      style={{ animationDelay: '0ms' }}
                    />
                    <div
                      className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"
                      style={{ animationDelay: '150ms' }}
                    />
                    <div
                      className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"
                      style={{ animationDelay: '300ms' }}
                    />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form
            onSubmit={handleSend}
            className="p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700"
          >
            <div className="flex space-x-4">
              <input
                ref={chatInputRef}
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500
                bg-white dark:bg-gray-700
                border-gray-300 dark:border-gray-600
                text-gray-800 dark:text-gray-200
                placeholder-gray-500 dark:placeholder-gray-400"
              />
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Send
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Right Panel with Table */}
      {selectedArtifact && (
        <div className="w-1/2 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 flex flex-col">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-white">{selectedArtifact.title}</h2>
            <button
              onClick={() => setSelectedArtifact(null)}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-gray-500 dark:text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex-1 overflow-auto">{renderArtifact(selectedArtifact)}</div>
        </div>
      )}
    </div>
  );
};

export default Chat;
